import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount';

import type {
  BidderFragment,
  CardFragment,
  OfferSideFragment,
  PrimaryOfferUpdate,
  PrimaryOfferUpdateFragment,
  PrimaryOfferUpdateVariables,
  SmallUserForSubscription_user,
  TokenAuctionUpdate,
  TokenAuctionUpdateFragment,
  TokenAuctionUpdateVariables,
  TokenOfferUpdate,
  TokenOfferUpdateFragment,
  TokenOfferUpdateVariables,
} from './__generated__/subscriptions.graphql';

const smallUserFragment = gql`
  fragment SmallUserForSubscription_user on User {
    slug
    suspended
    nickname
    pictureUrl(derivative: "avatar")
    profile {
      id
      badge {
        slug
        pictureUrl
        description
      }
    }
  }
` as TypedDocumentNode<SmallUserForSubscription_user>;
const bidderFragment = gql`
  fragment BidderFragment on BlockchainUser {
    ... on User {
      slug
      status
      ...SmallUserForSubscription_user
    }
    ... on AnonymousUser {
      id
      ethereumAddress
    }
  }
  ${smallUserFragment}
` as TypedDocumentNode<BidderFragment>;

const cardFragment = gql`
  fragment CardFragment on AnyCardInterface {
    slug
    user {
      slug
    }
    tokenOwner {
      id
      from
      blockchain
      address
      amounts {
        ...MonetaryAmountFragment_monetaryAmount
      }
      transferType
      user {
        slug
      }
      account {
        id
        accountable {
          ... on Node {
            id
          }
          ... on EthereumAccount {
            id
            address
          }
          ... on StarkwareAccount {
            id
            starkKey
          }
        }
        owner {
          ... on User {
            slug
          }
          ... on Contract {
            id
          }
        }
      }
    }
  }
  ${monetaryAmountFragment}
` as TypedDocumentNode<CardFragment>;

const tokenAuctionFragment = gql`
  fragment TokenAuctionUpdateFragment on TokenAuction {
    id
    open
    endDate
    currentPrice
    currency
    bidsCount
    minNextBid
    anyCards {
      slug
      ...CardFragment
      latestEnglishAuction {
        id
      }
    }
    bestBid {
      id
      userBidder {
        slug
        ...BidderFragment
      }
    }
    bids(first: 5) {
      nodes {
        id
        amounts {
          ...MonetaryAmountFragment_monetaryAmount
        }
        userBidder {
          slug
          ...BidderFragment
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
      totalCount
    }
  }
  ${cardFragment}
  ${bidderFragment}
  ${monetaryAmountFragment}
` as TypedDocumentNode<TokenAuctionUpdateFragment>;

const offerSideFragment = gql`
  fragment OfferSideFragment on TokenOfferSide {
    id
    amounts {
      ...MonetaryAmountFragment_monetaryAmount
    }
    anyCards {
      ...CardFragment
      slug
      liveSingleSaleOffer {
        id
      }
      myMintedSingleSaleOffer {
        id
      }
    }
  }
  ${monetaryAmountFragment}
  ${cardFragment}
` as TypedDocumentNode<OfferSideFragment>;

const tokenOfferFragment = gql`
  fragment TokenOfferUpdateFragment on TokenOffer {
    id
    endDate
    status
    senderSide {
      id
      ...OfferSideFragment
    }
    receiverSide {
      id
      ...OfferSideFragment
    }
  }
  ${offerSideFragment}
` as TypedDocumentNode<TokenOfferUpdateFragment>;

const primaryOfferUpdateFragment = gql`
  fragment PrimaryOfferUpdateFragment on TokenPrimaryOffer {
    id
    endDate
    status
    buyer {
      slug
      ...SmallUserForSubscription_user
    }
    price {
      ...MonetaryAmountFragment_monetaryAmount
    }
    signedAmount
  }
  ${monetaryAmountFragment}
  ${smallUserFragment}
` as TypedDocumentNode<PrimaryOfferUpdateFragment>;

export const tokenAuctionSubscription = gql`
  subscription TokenAuctionUpdate($sports: [Sport!]) {
    tokenAuctionWasUpdated(sports: $sports) {
      id
      ...TokenAuctionUpdateFragment
    }
  }
  ${tokenAuctionFragment}
` as TypedDocumentNode<TokenAuctionUpdate, TokenAuctionUpdateVariables>;

export const tokenOfferSubscription = gql`
  subscription TokenOfferUpdate($sports: [Sport!]) {
    tokenOfferWasUpdated(sports: $sports) {
      id
      ...TokenOfferUpdateFragment
    }
  }
  ${tokenOfferFragment}
` as TypedDocumentNode<TokenOfferUpdate, TokenOfferUpdateVariables>;

export const primaryOfferSubscription = gql`
  subscription PrimaryOfferUpdate($sports: [Sport!]) {
    primaryOfferWasUpdated(sports: $sports) {
      id
      ...PrimaryOfferUpdateFragment
    }
  }
  ${primaryOfferUpdateFragment}
` as TypedDocumentNode<PrimaryOfferUpdate, PrimaryOfferUpdateVariables>;
